// CRAP is inflated only when the CI fallow coverage step times out (0% scoring, see
// .github/workflows/fallow.yml); these branchy fns are fully unit-tested. Drop when fixed.
// fallow-ignore-file complexity unused-export unused-type
// Re-Export-Barrel: die neue API (EventTimeline + Typen, TES-1882) wird erst von Slice-2/3-Dashboards
// konsumiert; fallow sieht den Consumer (+ Test-Importe) nicht als Nutzung → unused-export/unused-type.
// (Issue-Kinds NUR auf der Direktiven-Zeile; Prosa als separater Kommentar, sonst stale suppression.)
// CheckMaty picker library (TES-1878) — config + provider bound to @plasser/ui.
// ---------------------------------------------------------------------------
// ONE picker for all dialogs. Each named wrapper is just config + provider; there
// is no per-dialog reimplementation. The default provider is the RLS-scoped
// Supabase provider; tests/demo inject a MockSearchProvider.

import * as React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { EntityPicker } from "@plasser/ui";
import type { EntityPickerProps, EntityTypeConfig, SearchProvider } from "@plasser/ui";

import {
  getChecklistTemplatePickerConfig,
  getGroupPickerConfig,
  getMachinePickerConfig,
  getMilestonePickerConfig,
  getPeoplePickerConfig,
} from "./configs";
import { createSupabaseSearchProvider } from "./SupabaseSearchProvider";
import type { ProviderContext } from "./SupabaseSearchProvider";

export * from "./configs";
export { createSupabaseSearchProvider } from "./SupabaseSearchProvider";
export type { ProviderContext } from "./SupabaseSearchProvider";

// TES-1882 (L6-UI): einheitliche, cross-level Event-Timeline (Mangel/Prüfung/Prüfpunkt).
export { EventTimeline } from "./EventTimeline";
export type { EventTimelineProps, TimelineEvent, TimelineActor } from "./EventTimeline";

// TES-1882 (L6-UI): generische server-side FilterBar für die Aufgaben-Dashboards.
export { FilterBar } from "./FilterBar";
export type { FilterBarProps, FilterField, FilterOption } from "./FilterBar";

// TES-1882 (L6-UI): virtualisierte DataTable für tausende Aufgaben.
export { DataTable } from "./DataTable";
export type { DataTableProps, DataTableColumn } from "./DataTable";

/** Props shared by all bound pickers (config is fixed per wrapper). */
export interface BoundPickerProps extends Omit<EntityPickerProps, "config" | "provider"> {
  /** Override the data source (tests/demo). Defaults to the Supabase provider. */
  provider?: SearchProvider;
  /** Context for the default Supabase provider (active team, current user). */
  providerContext?: ProviderContext;
}

function useBoundProvider(
  override: SearchProvider | undefined,
  ctx: ProviderContext | undefined,
): SearchProvider {
  const userId = ctx?.userId ?? null;
  const activeTeamId = ctx?.activeTeamId ?? null;
  return React.useMemo(
    () => override ?? createSupabaseSearchProvider({ userId, activeTeamId }),
    [override, userId, activeTeamId],
  );
}

// TES-2035: the config is resolved per-render from a t()-factory so labels follow the
// active language. `t` from useTranslation is stable across renders (only changes on
// languageChanged), so the memoized config identity stays stable for EntityPicker.
function makeBoundPicker(configFactory: (t: TFunction) => EntityTypeConfig, type: string) {
  function Bound({ provider, providerContext, ...rest }: BoundPickerProps): React.ReactElement {
    const { t } = useTranslation();
    const resolved = useBoundProvider(provider, providerContext);
    const config = React.useMemo(() => configFactory(t), [t]);
    return <EntityPicker config={config} provider={resolved} {...rest} />;
  }
  Bound.displayName = `${type}Picker`;
  return Bound;
}

export const GroupPicker = makeBoundPicker(getGroupPickerConfig, "group");
export const PeoplePicker = makeBoundPicker(getPeoplePickerConfig, "person");
export const MachinePicker = makeBoundPicker(getMachinePickerConfig, "machine");
export const MilestonePicker = makeBoundPicker(getMilestonePickerConfig, "milestone");
export const ChecklistTemplatePicker = makeBoundPicker(
  getChecklistTemplatePickerConfig,
  "checklist_template",
);

// Re-export the generic picker for ad-hoc configs.
export { EntityPicker } from "@plasser/ui";
